import {BuildingClient} from "../../client/BuildingClient"
import {DataClient, DateRange, ReportType} from "../../client/DataClient"
import {useEffect, useState} from "react"
import {Building} from "../BuildingStatus"
import fileDownload from "js-file-download"
import {ISODateRange, LondonDateRange} from "../../ISODateRange"
import {MeterData} from "../MeterData"
import {BillingExport} from "../BillingExport"
import {baseLineMethodology, BillingExplanation} from "../BillingExplanation"
import {Result} from "../../client/Result"
import {UKPNSettlementExport} from "../UKPNSettlementExport"
import {LoadingIndicator} from "../common/LoadingIndicatior"
import {UploadBucketMapping, UploadHalfHourlyConsumption} from "../common/UploadComponents"
import {useEntitlements} from "../../EntitlementProvider"
import {Permission} from "../../Permission"
import {MeterApi, MeterClient} from "../../client/MeterClient"
import {EventStatsExport} from "../EventStatsExport"
import {CapacityMarketDropReport} from "../CapacityMarketDropReport"
import {BucketClient} from "../../client/BucketClient"
import {EMRSMeteredVolumesDownload} from "../EMRSMeteredVolumesDownload"
import {LayerGroupIcon, BoltIcon, CoinsIcon, ChartBarIcon, ChartLineIcon} from "../icons/Icons"
import {EMRComponentReport} from "../EMRComponentReport"
import {TimeRange} from "../common/TimeRangePicker"
import {useLocation} from "react-router-dom"
import {Filter} from "../common/Filter"
import {DateTime} from "../../time/DateTime"
import {CardSection} from "../common/Card";

export interface DataPageProps {
    buildingClient: BuildingClient
    meterClient: MeterClient
    bucketClient: BucketClient
    dataClient: DataClient
}

type FilterCategory = 'all' | 'consumption' | 'finance' | 'programme' | 'performance' | 'p415'

const FILTER_CATEGORIES = [
    {id: 'all' as const, label: 'All Sections', icon: LayerGroupIcon},
    {id: 'consumption' as const, label: 'Consumption Data', icon: BoltIcon},
    {id: 'finance' as const, label: 'Finance Data', icon: CoinsIcon},
    {id: 'programme' as const, label: 'Programme Analysis', icon: ChartBarIcon},
    {id: 'performance' as const, label: 'Performance Analysis', icon: ChartLineIcon}
]

function download(filename: string, result: Result<string | Blob>): Result<void> {
    return result.map(value => {
        fileDownload(value, filename)
    })
}

type ShouldShowSectionProps = {
    section: FilterCategory;
    activeFilter: FilterCategory;
    children: React.ReactNode;
};

const ShouldShowSection = ({section, activeFilter, children}: ShouldShowSectionProps) => {
    return activeFilter === "all" || activeFilter === section ? <>{children}</> : null;
}

export function DataPage({
                             buildingClient,
                             meterClient,
                             bucketClient,
                             dataClient,
                         }: DataPageProps) {
    const {hash} = useLocation()
    const [activeFilter, setActiveFilter] = useState<FilterCategory>(() => {
        return FILTER_CATEGORIES.find(el => el.id === hash.slice(1))?.id ?? "all"
    })

    const [buildings, setBuildings] = useState<{ id: number, name: string }[]>()
    const [meters, setMeters] = useState<MeterApi[]>()
    const [buckets, setBuckets] = useState<string[]>()
    const [cmuIds, setCmuIds] = useState<string[]>()

    useEffect(() => {
        const newFilter =
            FILTER_CATEGORIES.find(el => el.id === hash.slice(1))?.id ?? 'all'
        setActiveFilter(newFilter)
    }, [hash])

    useEffect(() => {
        buildingClient.retrieveBuildingsWithMeterMpans().then(bs => setBuildings(bs.map(b => ({
            id: b.id,
            name: b.name
        }))))
    }, [buildingClient])

    useEffect(() => {
        meterClient.retrieveMeters().then(ms => setMeters(ms))
    }, [meterClient])

    useEffect(() => {
        bucketClient.retrieveBuckets('CapacityMarket').then(b => setBuckets(b))
    }, [bucketClient])

    useEffect(() => {
        bucketClient.retrieveCmuIds().then(ids => setCmuIds(ids))
    }, [bucketClient])

    if (!meters || !buildings) {
        return <LoadingIndicator/>
    }

    return (
        <div>
            <Filter
                categories={FILTER_CATEGORIES}
                activeFilter={activeFilter}
                onFilterChange={s => setActiveFilter(s as FilterCategory)}
            />
            <ShouldShowSection section="consumption" activeFilter={activeFilter}>
                <ConsumptionSection meters={meters} dataClient={dataClient}/>
            </ShouldShowSection>
            <ShouldShowSection section="finance" activeFilter={activeFilter}>
                <FinanceSection dataClient={dataClient} buildings={buildings}/>
            </ShouldShowSection>
            <ShouldShowSection section="programme" activeFilter={activeFilter}>
                <ProgrammeSection dataClient={dataClient} buildings={buildings} cmuIds={cmuIds} buckets={buckets}/>
            </ShouldShowSection>
            <ShouldShowSection section="performance" activeFilter={activeFilter}>
                <PerformanceSection dataClient={dataClient} buildings={buildings}/>
            </ShouldShowSection>
        </div>
    )
}

const ConsumptionSection = ({meters, dataClient}) => {
    const entitlement = useEntitlements()

    async function downloadHalfHourlyData(mpan: string, start: string, end: string, format = 'hh'): Promise<Result<void>> {
        const result = await dataClient.retrieveMeterHalfHourlyConsumptionForMpan(mpan, new ISODateRange(start, end), format)
        if (format === 'EDFDeliveryData') return download(`${DateTime.local().toFormat("yyyy-MM-dd HH-mm-ss")} DeliveryData ${mpan}.csv`, result)
        else return download(`${mpan}_${start}_${end}_${format}.csv`, result)
    }

    return <CardSection header="Consumption Data" data-testid="consumption-data-section" className="mb-4 border-primary" headerClassName="text-light bg-primary">
        <Row>
            <Column>
                <MeterData
                    className="h-100"
                    data-testid={'halfHourlyMpanDownload'}
                    meters={meters.filter(m => m.readingsGranularities.includes('PT30M') && m.hasSiteAttached)}
                    download={downloadHalfHourlyData}
                    header={'Half Hour Consumption Data'}
                    formatOptions={[{label: "EDF Delivery Data", id: "EDFDeliveryData"}, {label: "Oaktree", id: "hh"}]}
                />
            </Column>
            {entitlement.has(Permission.WRITE_READINGS) && (
                <Column>
                    <UploadHalfHourlyConsumption
                        className="h-100"
                        onFileUpload={file => dataClient.uploadReadings(file)}
                    />
                </Column>
            )}
        </Row>
    </CardSection>
}

const FinanceSection = ({dataClient, buildings}) => {
    const entitlement = useEntitlements()

    async function downloadBillingExport(range: ISODateRange): Promise<Result<void>> {
        const result = await dataClient.retrieveBillingExport(range)
        return download(`billing_exports_${range.start}_${range.end}.csv`, result)
    }

    async function dfsSettlementReport(range: LondonDateRange): Promise<Result<void>> {
        const result = await dataClient.retrieveDfsSettlement(range)
        return download(`dfs_settlement_report_${range.range.start}_${range.range.end}.csv`, result)
    }

    async function downloadBillingExplanation(buildingId: number, range: ISODateRange, methodology: baseLineMethodology): Promise<Result<void>> {
        const result = await dataClient.retrieveBillingExplanation(buildingId, range, methodology)
        return download(`explain_billing_${methodology}_${buildingId}_${range.start}_${range.end}.csv`, result)
    }

    return <CardSection header="Finance Data" data-testid="finance-data-section" className="mb-4 border-primary" headerClassName="text-light bg-primary">
        <Row>
            <Column>
                <BillingExport
                    className="h-100"
                    download={downloadBillingExport}
                    testIdPrefix={'billingExport'}
                    cardHeader={'Billing Export'}
                />
            </Column>
            <Column>
                <BillingExplanation
                    className="h-100"
                    buildingsSource={buildings}
                    downloadBaseline={downloadBillingExplanation}
                    header={'Explain Billing'}
                />
            </Column>
            {entitlement.has(Permission.READ_BILLING) && (
                <Column>
                    <BillingExport
                        className="h-100"
                        download={(iso) => dfsSettlementReport(new LondonDateRange(iso.start, iso.end))}
                        testIdPrefix={'dfsSettlementReport'}
                        cardHeader={'DFS Settlement'}
                    />
                </Column>
            )}
        </Row>
    </CardSection>
}

const ProgrammeSection = ({dataClient, buildings, buckets, cmuIds}) => {
    const entitlement = useEntitlements()

    async function downloadUKPNSettlement(range: DateRange, building: Building): Promise<Result<void>> {
        const result = await dataClient.readingsUKPNSettlement(building.id, range)
        return download(`UKPN_settlement_${range.start.toISODate()}_${range.end.toISODate()}.csv`, result)
    }

    async function downloadCapacityMarketDropReport(range: ISODateRange, report: ReportType, bucketId: string, timeRange: TimeRange | undefined): Promise<Result<void>> {
        const result = await dataClient.capacityMarketDropReportByBucket(bucketId, report, range, timeRange)
        return download(`CM_${bucketId}_${range.start}_${range.end}_${report}.csv`, result)
    }

    async function downloadCapacityMarketDropReportByCmu(range: ISODateRange, report: ReportType, cmuId: string, timeRange: TimeRange | undefined): Promise<Result<void>> {
        const result = await dataClient.capacityMarketDropReportByCmu(cmuId, report, range, timeRange)
        return download(`CM_${cmuId}_${range.start}_${range.end}_${report}.csv`, result)
    }

    async function downloadDsrCertificateConsumptionReport(range: ISODateRange, cmuId: string): Promise<Result<void>> {
        const result = await dataClient.retrieveMeterHalfHourlyConsumptionForBucketInElexonFormatForDsrCertificate(cmuId, range)
        return download(`emrs_metering_${cmuId}_${range.start}_${range.end}.zip`, result)
    }

    async function downloadEmrComponentReport(cmuId: string): Promise<Result<void>> {
        const result = await dataClient.capacityMarketEmrComponentReport(cmuId)
        return download(`emr_component_${cmuId}.csv`, result)
    }

    async function downloadEmrsMeteringPointComponentReport(cmuId: string): Promise<Result<void>> {
        const result = await dataClient.capacityMarketEmrsMeteringPointComponentReport(cmuId)
        return download(`emrs_metering_point_component_${cmuId}.csv`, result)
    }

    return <CardSection header="Programme Analysis" data-testid="programme-analysis-section" className="mb-4 border-primary" headerClassName="text-light bg-primary">
        <Row>
            <Column>
                <UKPNSettlementExport
                    className="h-100"
                    download={downloadUKPNSettlement}
                    buildings={buildings}
                />
            </Column>
            {buckets && (<Column>
                <CapacityMarketDropReport
                    className="h-100"
                    download={downloadCapacityMarketDropReport}
                    buckets={buckets}
                />
            </Column>)}
            {cmuIds && (<>
                <Column>
                    <CapacityMarketDropReport
                        className="h-100"
                        title={'CMU Capacity Market Drop Report'}
                        label={'CMU'}
                        download={downloadCapacityMarketDropReportByCmu}
                        buckets={cmuIds}
                    />
                </Column>
                <Column>
                    <EMRComponentReport
                        className="h-100"
                        download={downloadEmrComponentReport}
                        cmuIds={cmuIds}
                        cardTitle={'EMR Component Report'}
                        dataTestPrefix={'emrComponentReport'}
                    />
                </Column>
                <Column>
                    <EMRComponentReport
                        className="h-100"
                        download={downloadEmrsMeteringPointComponentReport}
                        cmuIds={cmuIds}
                        cardTitle={'EMRS Metering Point Component Report'}
                        dataTestPrefix={'emrsMeteringPointComponentReport'}
                    />
                </Column>
                <Column>
                    <EMRSMeteredVolumesDownload
                        className="h-100"
                        download={downloadDsrCertificateConsumptionReport}
                        cmuIds={cmuIds}
                    />
                </Column>
            </>)}
            {entitlement.has(Permission.WRITE_BUCKET) && (<Column>
                <UploadBucketMapping
                    className="h-100"
                    onFileUpload={(file, bucketId) => dataClient.uploadBucket(file, bucketId)}
                />
            </Column>)}
        </Row>
    </CardSection>
}

const PerformanceSection = ({dataClient, buildings}) => {
    const entitlement = useEntitlements()

    async function downloadEventsStatsJson(range: DateRange, building: Building): Promise<Result<void>> {
        const result = await dataClient.eventStats(building.id, range)
        return download(`EventStats_${building.name}_${range.start.toISODate()}_${range.end.toISODate()}.zip`, result)
    }

    return <CardSection header="Performance Analysis" data-testid="performance-analysis-section" className="mb-4 border-primary" headerClassName="text-light bg-primary">
        <Row>
            {entitlement.has(Permission.READ_EVENT_STATS) && (
                <Column>
                    <EventStatsExport
                        className="h-100"
                        download={downloadEventsStatsJson}
                        buildings={buildings}
                    />
                </Column>
            )}
        </Row>
    </CardSection>
}

const Row = ({children}) => <div className="row g-3">{children}</div>

const Column = ({children}) => <div className="col-12 col-md-6 col-lg-4">{children}</div>